import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SearchComboItemModel } from '../models/search-combo-item-model';
import { ConstantsService } from './constants.service';

@Injectable({
  providedIn: 'root'
})
export class FormsService {

  constructor(private fb: FormBuilder, private constants: ConstantsService) { }

  readonly keywordLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = typeof control.value === 'string' ? control.value.trim() : '';

    if (value.length === 0 || value.length >= 3) {
      return null;
    }

    return { invalidLength: true };
  };

  createKeywordControl(value: string = ''): FormControl {
    return new FormControl(value, [this.keywordLengthValidator]);
  }

  controlHasKeywordLengthError(control: AbstractControl | null | undefined): boolean {
    return !!control && control.hasError('invalidLength') && (control.touched || control.dirty);
  }

  initSearchForm(): FormGroup {
    return this.fb.group({
      keywords: this.createKeywordControl(),
      legacyLibFilter: new FormControl(true),
      mainLibFilter: new FormControl(true),
      [this.constants.property_operator_any]: this.fb.array([]),
      [this.constants.property_operator_all]: this.fb.array([])
    });
  }

  newProp(): FormGroup {
    return this.fb.group({
      propertyItem: '',
      keywords: this.createKeywordControl(),
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      dateOperator: 'between',
      textOperator: this.constants.text_operator_contains,
      isDate: false
    });
  }

  props(searchForm: FormGroup, operator: string): FormArray {

    if (operator === this.constants.property_operator_any) {
      return searchForm.get(this.constants.property_operator_any) as FormArray;
    }

    return searchForm.get(this.constants.property_operator_all) as FormArray;
  }

  updateProp(prop: SearchComboItemModel): FormGroup {
    return this.fb.group({
      propertyItem: prop.propertyItem,
      keywords: this.createKeywordControl(prop.keywords),
      startDate: prop.startDate,
      endDate: prop.endDate,
      startTime: prop.startTime,
      endTime: prop.endTime,
      dateOperator: prop.dateOperator,
      textOperator: prop.textOperator,
      isDate: false
    });
  }

  updateDateProp(prop: SearchComboItemModel): FormGroup {
    return this.fb.group({
      propertyItem: prop.propertyItem,
      keywords: this.createKeywordControl(prop.keywords),
      startDate: prop.startDate,
      endDate: prop.endDate,
      startTime: prop.startTime,
      endTime: prop.endTime,
      dateOperator: prop.dateOperator,
      textOperator: prop.textOperator,
      isDate: true
    });
  }

  pushProp(searchForm: FormGroup, item: FormGroup, operator: string) {
    this.props(searchForm, operator).push(item)
  }

  addProp(searchForm: FormGroup, operator: string) {
    this.props(searchForm, operator).push(this.newProp());
  }

  removeProp(searchForm: FormGroup, index: number, operator: string) {
    this.props(searchForm, operator).removeAt(index);
  }

  clearProp(searchForm: FormGroup, index: number, operator: string) {
    this.props(searchForm, operator).removeAt(index);

    this.props(searchForm, operator).insert(index, this.newProp());
  }

  getTimeIntervals() : string[]{
    const intervals: string[] = [];
    intervals.push('');
    for (let hour = 0; hour < 24; hour ++){
      for(let minutes = 0; minutes <60; minutes +=15){
        const rawHour = hour % 12 === 0 ? 12 : hour % 12;
        const displayHour = rawHour.toString().padStart(2,'0');
        const ampm = hour < 12 ? 'AM' : 'PM';
        const mm = minutes.toString().padStart(2, '0');
        intervals.push(`${displayHour}:${mm} ${ampm}`);
      }
    }
    return intervals;
  }
}
